<template>
  <a-modal
    v-if="visible"
    title="Import Invoice"
    :visible="visible"
    :destroy-on-close="false"
    width="600px"
    data-testid="shifts-import-invoice-modal"
    @cancel="closeModal"
  >
    <div class="flex flex-column gap-3">
      <a-alert
        message="Export any invoice from the Clipboard portal as a CSV file and upload it
      in the form so we can apply all shift adjustments. After you run it, the
      shifts in Clipboard and in ePeople will match."
        type="info"
        show-icon
      />

      <a-upload
        class="clipboard-invoice-upload"
        :showUploadList="false"
        :before-upload="beforeUpload"
        accept=".csv"
        type="drag"
        :disabled="loadingImport"
      >
        <div class="clipboard-invoice-upload-content">
          <a-icon :type="invoiceFile ? 'file-text' : 'inbox'" class="clipboard-invoice-upload-icon" />
          <div class="clipboard-invoice-upload-title">
            {{ invoiceFile ? invoiceFile.name : 'Click or drag CSV file to this area' }}
          </div>
          <div class="clipboard-invoice-upload-hint">
            {{
              invoiceFile
                ? 'Click or drop another file to replace it'
                : 'Select the invoice CSV exported from Clipboard'
            }}
          </div>
        </div>
      </a-upload>

      <div>
        <a-checkbox :disabled="loadingImport" v-model="is_create_payment">Create payments after import</a-checkbox>
        <div class="clipboard-payment-help-text">
          Payments will be created only for valid shifts. If a shift is incorrect or cannot be paid, payment will not be
          created for that shift.
        </div>
      </div>

      <div>
        <a-checkbox :disabled="loadingImport" v-model="is_sync_cdph">Sync CDPH 530 after import</a-checkbox>
        <div class="clipboard-payment-help-text">
          CDPH 530 forms will be synced only for imported shifts. If this option is off, shifts will be imported without
          syncing CDPH 530 forms.
        </div>
      </div>

      <div>
        <a-checkbox :disabled="loadingImport" v-model="not_update_shifts">Do not update existing shifts</a-checkbox>
        <div class="clipboard-payment-help-text">
          Use this when re-importing an invoice after compliance edits. Existing shifts already found in ePeople will
          stay unchanged, while payments can still be created for eligible shifts from the invoice.
        </div>
      </div>
    </div>
    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closeModal">Close</a-button>

        <a-button :loading="loadingImport" :disabled="!invoiceFile" type="primary" @click="importSelectedFile">
          {{ importButtonText }}
        </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { Alert, Button, Checkbox, Icon, message, Modal, notification, Upload } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'

const { mapActions: shiftActions } = createNamespacedHelpers('shifts')

export default {
  components: {
    'a-modal': Modal,
    'a-button': Button,
    'a-upload': Upload,
    'a-checkbox': Checkbox,
    'a-alert': Alert,
    'a-icon': Icon
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loadingImport: false,
      invoiceFile: null,

      is_create_payment: false,
      is_sync_cdph: false,
      not_update_shifts: false
    }
  },
  computed: {
    importButtonText() {
      const actions = []

      if (this.is_create_payment) {
        actions.push('Create Payment')
      }

      if (this.is_sync_cdph) {
        actions.push('Sync CDPH 530')
      }

      return ['Import Invoice', ...actions].join(' & ')
    }
  },
  methods: {
    ...shiftActions(['postImportClipboard']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    getImportActionLinks(h, { shift_ids = [], start_date, end_date, payment_id }) {
      return [
        shift_ids.length &&
          h(
            'a-button',
            {
              props: {
                type: 'link',
                size: 'small'
              },
              class: 'p-0 w-fit',
              on: {
                click: () =>
                  this.$router
                    .push({
                      path: '/shifts',
                      query: { shift_ids }
                    })
                    .catch(() => {})
              }
            },
            'View Imported Invoice Shifts'
          ),
        start_date &&
          h(
            'a-button',
            {
              props: {
                type: 'link',
                size: 'small'
              },
              class: 'p-0 w-fit',
              on: {
                click: () =>
                  this.$router
                    .replace({
                      query: { start_date, end_date },
                      path: '/shifts'
                    })
                    .catch(() => {})
              }
            },
            'View ALL Shifts in this Date Range'
          ),
        this.is_create_payment &&
          payment_id &&
          h(
            'a-button',
            {
              props: {
                type: 'link',
                size: 'small'
              },
              class: 'p-0 w-fit',
              on: {
                click: () =>
                  this.$router
                    .push({
                      path: '/invoices',
                      query: {
                        request_ids: [payment_id]
                      }
                    })
                    .catch(() => {})
              }
            },
            'View Invoice'
          )
      ].filter(Boolean)
    },

    getImportResultMessage({ isPartialImport, paymentWasCreated }) {
      if (isPartialImport) {
        if (this.is_create_payment && !paymentWasCreated) {
          return 'You selected Create payments after import, but no payment was created. Download the import report for details.'
        }

        if (this.is_create_payment && paymentWasCreated) {
          return 'Some shifts need attention. A payment was created for eligible shifts. Download the import report for details.'
        }

        return 'Some shifts need attention. Download the import report for details.'
      }

      if (this.is_create_payment && paymentWasCreated) {
        if (this.is_sync_cdph) {
          return 'All shifts were imported, the payment was created successfully, and CDPH 530 forms were synced. You can review them here:'
        }

        return 'All shifts were imported, and the payment was created successfully. You can review them here:'
      }

      let successMessage = this.not_update_shifts
        ? 'The invoice was successfully imported. Existing shifts were left unchanged. You can review the shifts here:'
        : 'Shifts were successfully updated in the system. You can review them here:'

      if (this.is_create_payment && this.is_sync_cdph) {
        successMessage = this.not_update_shifts
          ? 'The invoice was successfully imported, existing shifts were left unchanged, payments were created for eligible shifts, and CDPH 530 forms were synced. You can review them here:'
          : 'Shifts were successfully updated, payments were created for eligible shifts, and CDPH 530 forms were synced. You can review them here:'
      } else if (this.is_create_payment) {
        successMessage = this.not_update_shifts
          ? 'The invoice was successfully imported, existing shifts were left unchanged, and payments were created for eligible shifts. You can review them here:'
          : 'Shifts were successfully updated, and payments were created for eligible shifts. You can review them here:'
      } else if (this.is_sync_cdph) {
        successMessage = this.not_update_shifts
          ? 'The invoice was successfully imported, existing shifts were left unchanged, and CDPH 530 forms were synced. You can review them here:'
          : 'Shifts were successfully updated, and CDPH 530 forms were synced. You can review them here:'
      }

      return successMessage
    },

    showImportResultNotification({ unprocessed_data_url, report_summary, shift_ids, start_date, end_date, payment_id }) {
      const hasFailed = Boolean(report_summary && report_summary.failed)
      const hasWarnings = Boolean(report_summary && report_summary.warning)
      const needsAttention = hasFailed || hasWarnings
      const paymentWasCreated = Boolean(payment_id)

      notification[needsAttention ? 'warning' : 'success']({
        message: hasFailed ? 'Partially Imported' : hasWarnings ? 'Imported with Warnings' : 'Success',
        description: h => {
          const resultMessage = this.getImportResultMessage({
            isPartialImport: needsAttention,
            paymentWasCreated
          })

          return h(
            'div',
            [
              h('p', {}, resultMessage),
              ...this.getImportActionLinks(h, {
                shift_ids,
                start_date,
                end_date,
                payment_id
              })
            ].filter(Boolean)
          )
        },
        duration: 0,
        btn: unprocessed_data_url
          ? h => {
              return h(
                'a-button',
                {
                  props: {
                    type: 'primary',
                    size: 'small',
                    icon: 'download'
                  },
                  on: {
                    click: () => window.open(unprocessed_data_url, '_blank')
                  }
                },
                'Download Import Report'
              )
            }
          : null
      })
    },

    closeModal() {
      this.$emit('update:visible', false)
      this.invoiceFile = null
    },

    handleDownload() {
      window.open(this.url, '_blank')
    },

    beforeUpload(file) {
      this.invoiceFile = file

      return false
    },

    importSelectedFile() {
      if (!this.invoiceFile) {
        return
      }

      this.import(this.invoiceFile)
    },

    async import(invoice_file) {
      const loadingMessage = message.loading('Clipboard Import Loading..', 0)

      this.loadingImport = true

      try {
        const {
          unprocessed_data_url,
          report_summary,
          shift_ids,
          period_start: start_date,
          period_end: end_date,
          payment_id
        } = await this.postImportClipboard({
          invoice_file,
          create_payment: this.is_create_payment,
          sync_cdph: this.is_sync_cdph,
          not_update_shifts: this.not_update_shifts
        })

        this.showImportResultNotification({
          unprocessed_data_url,
          report_summary,
          shift_ids,
          start_date,
          end_date,
          payment_id
        })
      } catch (error) {
        this.showNotification('error', 'Error', error.message || error)
      } finally {
        if (loadingMessage()) {
          loadingMessage().destroy()
        }

        this.loadingImport = false
        this.invoiceFile = null
      }
    }
  }
}
</script>

<style scoped>
.clipboard-invoice-upload {
  display: block;
}

.clipboard-invoice-upload-content {
  padding: 16px;
}

.clipboard-invoice-upload-icon {
  color: #1890ff;
  font-size: 32px;
}

.clipboard-invoice-upload-title {
  margin-top: 8px;
  color: var(--text-color-alpha8);
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  word-break: break-word;
}

.clipboard-invoice-upload-hint {
  margin-top: 4px;
  color: #8c8c8c;
  font-size: 12px;
  line-height: 18px;
}

.clipboard-payment-help-text {
  margin-top: 4px;
  padding-left: 24px;
  color: #8c8c8c;
  font-size: 12px;
  line-height: 18px;
}
</style>
